import { lazy, type ComponentType } from 'react';

const RELOAD_FLAG = 'chunk_reload_attempted';

/**
 * Wraps React.lazy() with automatic retry on chunk load failures.
 * When a new build is deployed, old chunk URLs become invalid.
 * This utility auto-reloads the page once to fetch fresh assets,
 * using a sessionStorage flag to prevent infinite reload loops.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const module = await importFn();
      // Successful load — clear any stale reload flag
      sessionStorage.removeItem(RELOAD_FLAG);
      return module;
    } catch (error) {
      // Check if we already tried reloading this session
      const hasReloaded = sessionStorage.getItem(RELOAD_FLAG);
      if (!hasReloaded) {
        sessionStorage.setItem(RELOAD_FLAG, 'true');
        window.location.reload();
        // Return a never-resolving promise to prevent React from rendering stale state
        return new Promise(() => {});
      }
      // Already reloaded once and still failing — let the error propagate to RouteErrorPage
      sessionStorage.removeItem(RELOAD_FLAG);
      throw error;
    }
  });
}
